// Quote Data Constants
// Migrated from Deal Calculator data.js

import type { PlanPricing, PortalPricing, OnboardingCosts, ExchangeRates, PlanType } from './types'

// Portal seat pricing
export const PORTAL_PRICING: Record<number, PortalPricing> = {
  0: { price: 0, costPrice: 0 },
  5: { price: 1500, costPrice: 300 },
  10: { price: 3000, costPrice: 300 },
  30: { price: 9000, costPrice: 300 },
}

// Plan pricing defaults
export const PLAN_PRICING: Record<PlanType, PlanPricing> = {
  Basic: { platformFees: 3000, implementationFee: 2499, minimumCommitment: 999 * 12 },
  Professional: { platformFees: 12000, implementationFee: 9999, minimumCommitment: 4999 * 12 },
  Enterprise: { platformFees: 60000, implementationFee: 24999, minimumCommitment: 9999 * 12 },
}

// Exchange rates (base: AUD)
export const EXCHANGE_RATES: ExchangeRates = {
  AUD: { AUD: 1, USD: 0.65, NZD: 1.09, EUR: 0.60, CAD: 0.88 },
  USD: { AUD: 1.54, USD: 1, NZD: 1.68, EUR: 0.92, CAD: 1.35 },
  NZD: { AUD: 0.92, USD: 0.60, NZD: 1, EUR: 0.55, CAD: 0.81 },
  EUR: { AUD: 1.67, USD: 1.09, NZD: 1.82, EUR: 1, CAD: 1.47 },
  CAD: { AUD: 1.14, USD: 0.74, NZD: 1.24, EUR: 0.68, CAD: 1 },
}

// CAC lookup by plan
export const CAC_LOOKUP: Record<PlanType, number> = {
  Basic: -9705,
  Professional: -17470,
  Enterprise: -69879,
}

// LTV multipliers by plan
export const LTV_MULTIPLIERS: Record<PlanType, number> = {
  Basic: 3,
  Professional: 5,
  Enterprise: 5,
}

// Onboarding costs by plan
export const ONBOARDING_LOOKUP: Record<PlanType, OnboardingCosts> = {
  Basic: { yr1: -761, ongoing: -1985 },
  Professional: { yr1: -1711, ongoing: -4750 },
  Enterprise: { yr1: -6845, ongoing: -9153 },
}

// Hosting costs by plan
export const HOSTING_LOOKUP: Record<PlanType, number> = {
  Basic: -1000,
  Professional: -2000,
  Enterprise: -3000,
}

// Plan options for select
export const PLAN_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Basic', label: 'Basic' },
  { value: 'Professional', label: 'Professional' },
  { value: 'Enterprise', label: 'Enterprise' },
]

// Currency options
export const CURRENCY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'AUD', label: 'AUD' },
  { value: 'USD', label: 'USD' },
  { value: 'NZD', label: 'NZD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'CAD', label: 'CAD' },
]

// Payment frequency options
export const PAYMENT_FREQUENCY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Annual', label: 'Annual' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Monthly', label: 'Monthly' },
]

// Contract term options (months)
export const CONTRACT_TERM_OPTIONS = [
  { value: '0', label: 'Select...' },
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
]

// Country options
export const COUNTRY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'PH', label: 'Philippines' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'IN', label: 'India' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'CN', label: 'China' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'OM', label: 'Oman' },
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'PT', label: 'Portugal' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'PL', label: 'Poland' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'GH', label: 'Ghana' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CL', label: 'Chile' },
  { value: 'AR', label: 'Argentina' },
  { value: 'IL', label: 'Israel' },
  { value: 'TR', label: 'Turkey' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'LA', label: 'Laos' },
  { value: 'BN', label: 'Brunei' },
  { value: 'FJ', label: 'Fiji' },
  { value: 'PG', label: 'Papua New Guinea' },
  { value: 'OTHER', label: 'Other' },
]

// Portal seats options
export const PORTAL_SEATS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '0', label: '0 seats' },
  { value: '5', label: '5 seats' },
  { value: '10', label: '10 seats' },
  { value: '30', label: '30 seats' },
]

// Support level options (annual cost)
export const SUPPORT_LEVEL_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '0', label: 'Standard (Included)' },
  { value: '12000', label: 'Premium ($12,000/yr)' },
  { value: '36000', label: 'Dedicated ($36,000/yr)' },
]

// Implementation support options
export const IMPLEMENTATION_SUPPORT_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '0', label: 'None' },
  { value: '24375', label: 'Standard ($24,375)' },
  { value: '48750', label: 'Extended ($48,750)' },
]

// Hosted OneSDK annual fee. The DB stores hosted_onesdk as a boolean flag, so the
// hydration mapper must restore this amount — keep it in sync with the option below.
export const HOSTED_ONESDK_ANNUAL_FEE = 6000

// Hosted OneSDK options
export const HOSTED_ONESDK_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: '0', label: 'No' },
  { value: String(HOSTED_ONESDK_ANNUAL_FEE), label: 'Yes ($6,000/yr)' },
]
